import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { createTheme, ThemeProvider as MuiThemeProvider } from '@mui/material/styles';
import { theme as appTheme } from '../theme/theme';

interface ThemeContextType {
  isDarkMode: boolean;
  toggleDarkMode: () => void;
  language: string;
  setLanguage: (lang: string) => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const useTheme = (): ThemeContextType => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

interface ThemeProviderProps {
  children: ReactNode;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  const [isDarkMode, setIsDarkMode] = useState(() => {
    const saved = localStorage.getItem('darkMode');
    return saved ? JSON.parse(saved) : false;
  });
  
  const [language, setLanguageState] = useState(() => {
    const saved = localStorage.getItem('language');
    return saved || 'zh-CN';
  });

  useEffect(() => {
    localStorage.setItem('darkMode', JSON.stringify(isDarkMode));
  }, [isDarkMode]);

  useEffect(() => {
    localStorage.setItem('language', language);
  }, [language]);

  const toggleDarkMode = () => {
    setIsDarkMode(!isDarkMode);
  };

  const setLanguage = (lang: string) => {
    setLanguageState(lang);
  };

  // 创建动态主题
  const muiTheme = createTheme({
    breakpoints: {
      values: {
        xs: 0,
        sm: 600,
        md: 900,
        lg: 1200,
        xl: 1536,
      },
    },
    spacing: 8, // 基础间距单位
    palette: {
      mode: isDarkMode ? 'dark' : 'light',
      primary: {
        main: appTheme.colors.primary,
      },
      secondary: {
        main: appTheme.colors.accent,
      },
      background: {
        default: isDarkMode ? '#121212' : '#ffffff',
        paper: isDarkMode ? '#1e1e1e' : '#ffffff',
      },
      text: {
        primary: isDarkMode ? '#ffffff' : '#000000',
        secondary: isDarkMode ? '#b3b3b3' : '#666666',
      },
    },
    typography: {
      fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
      h1: {
        fontSize: 'clamp(2rem, 5vw, 3.5rem)',
        fontWeight: 700,
        lineHeight: 1.2,
      },
      h2: {
        fontSize: 'clamp(1.75rem, 4vw, 2.5rem)',
        fontWeight: 600,
        lineHeight: 1.3,
      },
      h3: {
        fontSize: 'clamp(1.5rem, 3vw, 2rem)',
        fontWeight: 600,
        lineHeight: 1.4,
      },
      h4: {
        fontSize: 'clamp(1.25rem, 2.5vw, 1.5rem)',
        fontWeight: 500,
        lineHeight: 1.4,
      },
      h5: {
        fontSize: 'clamp(1.125rem, 2vw, 1.25rem)',
        fontWeight: 500,
        lineHeight: 1.5,
      },
      h6: {
        fontSize: 'clamp(1rem, 1.5vw, 1.125rem)',
        fontWeight: 500,
        lineHeight: 1.5,
      },
      body1: {
        fontSize: 'clamp(0.875rem, 1.2vw, 1rem)',
        lineHeight: 1.6,
      },
      body2: {
        fontSize: 'clamp(0.75rem, 1vw, 0.875rem)',
        lineHeight: 1.5,
      },
      button: {
        fontSize: 'clamp(0.875rem, 1.2vw, 1rem)',
        fontWeight: 500,
        textTransform: 'none',
      },
    },
    components: {
      MuiCard: {
        styleOverrides: {
          root: {
            backgroundColor: isDarkMode ? '#1e1e1e' : '#ffffff',
            boxShadow: isDarkMode 
              ? '0 4px 12px rgba(255,255,255,0.1)' 
              : '0 4px 12px rgba(0,0,0,0.1)',
            borderRadius: 12,
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundColor: isDarkMode ? '#1e1e1e' : '#ffffff',
            borderRadius: 12,
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: 8,
            padding: '12px 24px',
            '@media (max-width: 600px)': {
              padding: '10px 20px',
              fontSize: '0.875rem',
            },
          },
          sizeLarge: {
            padding: '16px 32px',
            '@media (max-width: 600px)': {
              padding: '12px 24px',
            },
          },
        },
      },
      MuiTextField: {
        styleOverrides: {
          root: {
            '& .MuiOutlinedInput-root': {
              borderRadius: 8,
              '@media (max-width: 600px)': {
                fontSize: '0.875rem',
              },
            },
          },
        },
      },
      MuiContainer: {
        styleOverrides: {
          root: {
            paddingLeft: 16,
            paddingRight: 16,
            '@media (max-width: 600px)': {
              paddingLeft: 12,
              paddingRight: 12,
            },
          },
        },
      },
    },
  });

  const value = {
    isDarkMode,
    toggleDarkMode,
    language,
    setLanguage,
  };

  return (
    <ThemeContext.Provider value={value}>
      <MuiThemeProvider theme={muiTheme}>
        {children}
      </MuiThemeProvider>
    </ThemeContext.Provider>
  );
};